import React, { useState } from "react";
import { api } from "../lib/api";
import { useI18n, LANGS } from "../lib/i18n.jsx";
import { PageHeader } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { HardHat, Building, Image, Languages, LayoutTemplate, UserCog, Network,
  Users, Shield, Building2, Boxes, Check, Plus, Trash2, ArrowRight, ArrowLeft, PartyPopper } from "lucide-react";

const TEMPLATES = [
  { key: "construction", name: "Construction company", desc: "Roles, positions, units + full electrical & finishing work tree, norms & prices." },
  { key: "minimal", name: "Minimal", desc: "Core roles (worker, foreman, director, admin) + units. Build works yourself." },
  { key: "empty", name: "Empty", desc: "Just worker + admin roles. Start from a blank canvas." },
];
const ROLE_OPTIONS = ["worker", "foreman", "prorab", "site_manager", "project_manager", "director"];

const STEPS = [
  { icon: Building, key: "Company name" }, { icon: Image, key: "Logo & color" },
  { icon: Languages, key: "Main language" }, { icon: Languages, key: "Languages" },
  { icon: LayoutTemplate, key: "Template" }, { icon: UserCog, key: "Administrator" },
  { icon: Network, key: "Structure" }, { icon: Users, key: "Employees" },
  { icon: Shield, key: "Roles" }, { icon: Building2, key: "Projects" }, { icon: Boxes, key: "Works" },
];

export default function Onboarding() {
  const { t, tn } = useI18n();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState({
    company: { name: "", app_name: "", logo_url: "", primary_color: "#D97706" },
    default_language: "ru", languages: ["ru", "en"], template: "construction",
    admin: { name: "", email: "", password: "Piche2026!" },
    departments: [{ name: { en: "Construction", ru: "Строительство" } }],
    teams: [{ name: { en: "Brigade #1", ru: "Бригада №1" }, department_index: 0 }],
    employees: [], projects: [{ name: "", address: "", client: "" }],
    roles: ["worker", "foreman", "prorab", "site_manager", "project_manager", "director"],
  });

  const up = (patch) => setD((p) => ({ ...p, ...patch }));
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const finish = async () => {
    setBusy(true);
    try {
      const payload = { ...d, company: { ...d.company, default_language: d.default_language, languages: d.languages } };
      const { data } = await api.post("/onboarding", payload);
      setDone(data);
      toast.success(t("company_ready"));
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="tactical-card p-8 text-center kpi-accent">
          <PartyPopper className="mx-auto text-[var(--amber)]" size={48} />
          <h2 className="font-display text-2xl font-bold mt-3">{done.company_name} is ready!</h2>
          <p className="text-[var(--text-3)] mt-1">Provisioned an isolated company tenant.</p>
          <div className="grid grid-cols-3 gap-3 my-5">
            <Stat label="Roles" value={done.roles} /><Stat label="Employees" value={done.employees} /><Stat label="Projects" value={done.projects} />
          </div>
          <div className="bg-[var(--bg)] rounded-lg p-4 text-left text-sm">
            <div className="font-semibold mb-1">Admin login for the new company</div>
            <div className="font-mono-num">{done.admin_email}</div>
            <div className="font-mono-num text-[var(--text-3)]">{d.admin.password}</div>
          </div>
          <Button className="mt-5 bg-[var(--amber)] text-white" onClick={() => { setDone(null); setStep(0); }} data-testid="onboard-again">{t("create_another")}</Button>
        </div>
      </div>
    );
  }

  const StepIcon = STEPS[step].icon;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Company onboarding" subtitle={`Step ${step + 1} / ${STEPS.length}`} />

      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {STEPS.map((s, i) => {
          const Ic = s.icon;
          return (
            <button key={i} data-testid={`step-nav-${i}`} onClick={() => setStep(i)}
              className={`flex-1 min-w-[40px] h-1.5 rounded-full transition-colors ${i <= step ? "bg-[var(--amber)]" : "bg-[var(--border)]"}`} title={s.key} />
          );
        })}
      </div>

      <div className="tactical-card p-6 min-h-[320px]">
        <div className="flex items-center gap-2 mb-5 font-display text-lg font-bold">
          <StepIcon className="text-[var(--amber)]" size={22} /> {STEPS[step].key}
        </div>

        {step === 0 && (
          <div className="space-y-3">
            <Label>{t("company_name")}</Label>
            <Input data-testid="ob-name" value={d.company.name} onChange={(e) => up({ company: { ...d.company, name: e.target.value, app_name: e.target.value } })} placeholder="SIA BuildCorp" />
            <Label>App name (shown in header)</Label>
            <Input data-testid="ob-appname" value={d.company.app_name} onChange={(e) => up({ company: { ...d.company, app_name: e.target.value } })} placeholder="BuildCorp" />
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <Label>{t("logo")} URL</Label>
            <Input data-testid="ob-logo" value={d.company.logo_url} onChange={(e) => up({ company: { ...d.company, logo_url: e.target.value } })} placeholder="https://…/logo.png" />
            <Label>{t("primary_color")}</Label>
            <div className="flex items-center gap-2">
              <input type="color" data-testid="ob-color" value={d.company.primary_color} onChange={(e) => up({ company: { ...d.company, primary_color: e.target.value } })} className="h-10 w-16" />
              <Input value={d.company.primary_color} onChange={(e) => up({ company: { ...d.company, primary_color: e.target.value } })} />
            </div>
            {d.company.logo_url && <img src={d.company.logo_url} alt="logo" className="h-16 mt-2 object-contain" />}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2">
            <Label>Main / default language</Label>
            {LANGS.map((l) => (
              <button key={l.code} data-testid={`ob-mainlang-${l.code}`} onClick={() => up({ default_language: l.code, languages: Array.from(new Set([l.code, ...d.languages])) })}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left font-semibold ${d.default_language === l.code ? "border-[var(--amber)] bg-amber-50" : "border-[var(--border)]"}`}>
                <span className="text-xl">{l.flag}</span> {l.name}
                {d.default_language === l.code && <Check size={16} className="ml-auto text-[var(--amber)]" />}
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2">
            <Label>Additional languages available in the app</Label>
            {LANGS.map((l) => (
              <label key={l.code} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[var(--border)] cursor-pointer">
                <Checkbox data-testid={`ob-lang-${l.code}`} checked={d.languages.includes(l.code)} disabled={l.code === d.default_language}
                  onCheckedChange={(c) => up({ languages: c ? Array.from(new Set([...d.languages, l.code])) : d.languages.filter((x) => x !== l.code) })} />
                <span className="text-xl">{l.flag}</span> {l.name}
                {l.code === d.default_language && <span className="ml-auto text-xs text-[var(--text-3)]">main</span>}
              </label>
            ))}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            {TEMPLATES.map((tp) => (
              <button key={tp.key} data-testid={`ob-tpl-${tp.key}`} onClick={() => up({ template: tp.key })}
                className={`w-full text-left p-4 rounded-lg border ${d.template === tp.key ? "border-[var(--amber)] bg-amber-50" : "border-[var(--border)]"}`}>
                <div className="font-display font-bold flex items-center gap-2">{tp.name} {d.template === tp.key && <Check size={16} className="text-[var(--amber)]" />}</div>
                <div className="text-sm text-[var(--text-3)] mt-1">{tp.desc}</div>
              </button>
            ))}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <Label>{t("admin_name")}</Label>
            <Input data-testid="ob-admin-name" value={d.admin.name} onChange={(e) => up({ admin: { ...d.admin, name: e.target.value } })} placeholder="Jane Doe" />
            <Label>{t("admin_email")}</Label>
            <Input data-testid="ob-admin-email" value={d.admin.email} onChange={(e) => up({ admin: { ...d.admin, email: e.target.value } })} placeholder="admin@buildcorp.com" />
            <Label>{t("temp_password")}</Label>
            <Input data-testid="ob-admin-pass" value={d.admin.password} onChange={(e) => up({ admin: { ...d.admin, password: e.target.value } })} />
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <ListEditor label="Departments" testid="dept" rows={d.departments}
              render={(row, i) => <Input value={row.name?.en || ""} data-testid={`ob-dept-${i}`} placeholder="Department name"
                onChange={(e) => { const arr = [...d.departments]; arr[i] = { name: { en: e.target.value, ru: e.target.value } }; up({ departments: arr }); }} />}
              onAdd={() => up({ departments: [...d.departments, { name: { en: "" } }] })}
              onDel={(i) => up({ departments: d.departments.filter((_, j) => j !== i) })} />
            <ListEditor label="Teams / brigades" testid="team" rows={d.teams}
              render={(row, i) => <Input value={row.name?.en || ""} data-testid={`ob-team-${i}`} placeholder="Team name"
                onChange={(e) => { const arr = [...d.teams]; arr[i] = { ...arr[i], name: { en: e.target.value, ru: e.target.value } }; up({ teams: arr }); }} />}
              onAdd={() => up({ teams: [...d.teams, { name: { en: "" }, department_index: 0 }] })}
              onDel={(i) => up({ teams: d.teams.filter((_, j) => j !== i) })} />
          </div>
        )}

        {step === 7 && (
          <ListEditor label="Employees to import" testid="emp" rows={d.employees}
            render={(row, i) => (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
                <Input data-testid={`ob-emp-first-${i}`} value={row.first_name || ""} placeholder="First" onChange={(e) => updRow(d, up, "employees", i, { first_name: e.target.value })} />
                <Input data-testid={`ob-emp-last-${i}`} value={row.last_name || ""} placeholder="Last" onChange={(e) => updRow(d, up, "employees", i, { last_name: e.target.value })} />
                <Input data-testid={`ob-emp-email-${i}`} value={row.email || ""} placeholder="email" onChange={(e) => updRow(d, up, "employees", i, { email: e.target.value })} />
                <select data-testid={`ob-emp-role-${i}`} className="border rounded-md h-9 px-2 text-sm" value={row.role_key || "worker"} onChange={(e) => updRow(d, up, "employees", i, { role_key: e.target.value })}>
                  {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}
            onAdd={() => up({ employees: [...d.employees, { first_name: "", last_name: "", email: "", role_key: "worker" }] })}
            onDel={(i) => up({ employees: d.employees.filter((_, j) => j !== i) })} />
        )}

        {step === 8 && (
          <div className="space-y-2">
            <Label>Roles to create for this company</Label>
            {ROLE_OPTIONS.map((r) => (
              <label key={r} className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-[var(--border)] cursor-pointer capitalize">
                <Checkbox data-testid={`ob-role-${r}`} checked={d.roles.includes(r)}
                  onCheckedChange={(c) => up({ roles: c ? [...d.roles, r] : d.roles.filter((x) => x !== r) })} />
                {r.replace("_", " ")}
              </label>
            ))}
            <p className="text-xs text-[var(--text-3)]">Admin + Director are always created. You can fully edit roles & scopes later in Roles.</p>
          </div>
        )}

        {step === 9 && (
          <ListEditor label="Projects / objects" testid="proj" rows={d.projects}
            render={(row, i) => (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1">
                <Input data-testid={`ob-proj-name-${i}`} value={row.name || ""} placeholder="Name" onChange={(e) => updRow(d, up, "projects", i, { name: e.target.value })} />
                <Input data-testid={`ob-proj-addr-${i}`} value={row.address || ""} placeholder="Address" onChange={(e) => updRow(d, up, "projects", i, { address: e.target.value })} />
                <Input data-testid={`ob-proj-client-${i}`} value={row.client || ""} placeholder="Client" onChange={(e) => updRow(d, up, "projects", i, { client: e.target.value })} />
              </div>
            )}
            onAdd={() => up({ projects: [...d.projects, { name: "", address: "", client: "" }] })}
            onDel={(i) => up({ projects: d.projects.filter((_, j) => j !== i) })} />
        )}

        {step === 10 && (
          <div className="text-center py-6">
            <Boxes className="mx-auto text-[var(--amber)]" size={40} />
            <p className="mt-3 font-semibold">Work hierarchy from the "{d.template}" template</p>
            <p className="text-sm text-[var(--text-3)] mt-1">
              {d.template === "construction" ? "Electrical & Finishing groups with subgroups, works, units, norms and prices will be created." : "No preset works — build your groups/subgroups/works in the Works constructor."}
            </p>
            <p className="text-sm text-[var(--text-3)] mt-4">Review everything, then provision the company.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-5">
        <Button variant="outline" onClick={prev} disabled={step === 0} data-testid="ob-prev" className="gap-1"><ArrowLeft size={16} /> {t("back")}</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} data-testid="ob-next" className="bg-[var(--amber)] text-white gap-1">{t("next")} <ArrowRight size={16} /></Button>
        ) : (
          <Button onClick={finish} disabled={busy || !d.company.name || !d.admin.email} data-testid="ob-finish" className="bg-[var(--green)] text-white gap-2">
            <Check size={16} /> Provision company
          </Button>
        )}
      </div>
    </div>
  );
}

function updRow(d, up, key, i, patch) {
  const arr = [...d[key]]; arr[i] = { ...arr[i], ...patch }; up({ [key]: arr });
}
function Label({ children }) { return <label className="text-xs uppercase font-semibold text-[var(--text-3)] block">{children}</label>; }
function Stat({ label, value }) {
  return <div className="tactical-card p-3"><div className="font-mono-num text-2xl font-bold">{value}</div><div className="text-xs text-[var(--text-3)]">{label}</div></div>;
}
function ListEditor({ label, rows, render, onAdd, onDel, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>{label}</Label>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`ob-add-${testid}`} className="gap-1"><Plus size={14} /> Add</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-[var(--text-3)]">None yet.</div>}
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            {render(row, i)}
            <button onClick={() => onDel(i)} data-testid={`ob-del-${testid}-${i}`} className="text-[var(--red)] shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
